import React from 'react';

export const CardSkeleton = ({ count = 6 }) => {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
      gap: '24px'
    }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          padding: '0',
          overflow: 'hidden',
          borderRadius: '12px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          animation: 'pulse 1.5s ease-in-out infinite',
          animationDelay: `${i * 0.1}s`
        }}>
          <div style={{ height: '220px', background: 'rgba(255,255,255,0.05)' }} />
          <div style={{ padding: '24px' }}>
            <div style={{ height: '24px', width: '70%', background: 'rgba(255,255,255,0.1)', borderRadius: '6px', marginBottom: '12px' }} />
            <div style={{ height: '16px', width: '100%', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', marginBottom: '8px' }} />
            <div style={{ height: '16px', width: '90%', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', marginBottom: '16px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ height: '28px', width: '80px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }} />
              <div style={{ height: '28px', width: '80px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const PageHeaderSkeleton = () => {
  return (
    <div style={{ 
      marginBottom: '40px',
      padding: '40px 30px',
      borderRadius: '16px',
      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.1) 100%)',
      border: '1px solid rgba(139, 92, 246, 0.3)',
      animation: 'pulse 1.5s ease-in-out infinite'
    }}>
      <div style={{ height: '40px', width: '300px', background: 'rgba(255,255,255,0.1)', borderRadius: '8px', marginBottom: '12px' }} />
      <div style={{ height: '20px', width: '500px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }} />
    </div>
  );
};

export const SearchBarSkeleton = () => {
  return (
    <div style={{ marginBottom: '35px' }}>
      <div style={{ 
        height: '50px', 
        maxWidth: '600px', 
        background: 'rgba(255,255,255,0.05)', 
        borderRadius: '12px',
        animation: 'pulse 1.5s ease-in-out infinite'
      }} />
    </div>
  );
};

export const LoadingOverlay = ({ message = 'Loading...' }) => {
  return (
    <div style={{
      position: 'absolute',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      borderRadius: '16px'
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px'
      }}>
        <div style={{ 
          width: '60px',
          height: '60px',
          border: '4px solid rgba(255,255,255,0.2)',
          borderTop: '4px solid var(--accent)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: 'white', fontWeight: '600', fontSize: '1.1rem' }}>{message}</p>
      </div>
    </div>
  );
};

export const FullPageLoader = () => {
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px'
      }}>
        <div style={{ 
          width: '80px',
          height: '80px',
          border: '6px solid var(--border-color)',
          borderTop: '6px solid var(--btn-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ 
          color: 'var(--text-primary)', 
          fontWeight: '600', 
          fontSize: '1.2rem' 
        }}>
          Loading...
        </p>
      </div>
    </div>
  );
};

export const ListItemSkeleton = ({ count = 5 }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{
          padding: '20px',
          borderRadius: '12px',
          background: 'var(--card-bg)',
          border: '1px solid var(--border)',
          animation: 'pulse 1.5s ease-in-out infinite',
          animationDelay: `${i * 0.1}s`
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ 
              width: '60px', 
              height: '60px', 
              borderRadius: '50%', 
              background: 'rgba(255,255,255,0.05)' 
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ 
                height: '20px', 
                width: '40%', 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: '6px', 
                marginBottom: '8px' 
              }} />
              <div style={{ 
                height: '16px', 
                width: '70%', 
                background: 'rgba(255,255,255,0.05)', 
                borderRadius: '6px' 
              }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const TableSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      overflow: 'hidden'
    }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: '16px',
          padding: '16px',
          borderBottom: i < rows - 1 ? '1px solid var(--border)' : 'none',
          animation: 'pulse 1.5s ease-in-out infinite',
          animationDelay: `${i * 0.1}s`
        }}>
          {Array.from({ length: columns }).map((_, j) => (
            <div key={j} style={{
              height: '20px',
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '6px'
            }} />
          ))}
        </div>
      ))}
    </div>
  );
};

export const AvatarSkeleton = ({ size = '60px' }) => {
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: 'var(--banner-placeholder-bg)',
      animation: 'pulse 1.5s ease-in-out infinite'
    }} />
  );
};

export const TextSkeleton = ({ width = '100%', height = '16px', marginBottom = '8px' }) => {
  return (
    <div style={{
      width,
      height,
      background: 'rgba(255,255,255,0.05)',
      borderRadius: '6px',
      marginBottom,
      animation: 'pulse 1.5s ease-in-out infinite'
    }} />
  );
};

export const PageLoader = () => {
  return (
    <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '30px 20px' }}>
      <PageHeaderSkeleton />
      <SearchBarSkeleton />
      <CardSkeleton />
    </div>
  );
};

export default FullPageLoader;
